/**
 * Agency Service
 * Fetches agency detail and sub-users for agency home
 *
 * @author Varun Pandey
 */

import apiClient from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';

export interface AgencyDetailArtist {
  slug: string;
  professional_name: string;
  profile_pic: string | null;
}

export interface AgencyDetailData {
  role: string;
  agency_name: string;
  gstin_number: string;
  artists: AgencyDetailArtist[];
}

export interface AgencyDetailResponse {
  success: boolean;
  data: AgencyDetailData;
}

export interface AgencySubUser {
  id: number;
  email: string;
  phone: string;
  /** API returns full_name; first_name/last_name may be absent */
  full_name?: string;
  first_name?: string;
  last_name?: string;
  can_view: boolean;
  can_edit: boolean;
  artist_slugs: string[];
}

export interface AgencySubUsersResponse {
  success: boolean;
  data: AgencySubUser[];
}

export interface AddAgencyArtistPayload {
  user: {
    professional_name: string;
  };
  category: string;
  subcategories: number[];
  eventTypes: number[];
}

/**
 * Fetch agency detail (GET api/v2/agency/detail/).
 */
export async function getAgencyDetail(): Promise<AgencyDetailData | null> {
  const response = await apiClient.get<AgencyDetailResponse>(API_ENDPOINTS.AUTH.AGENCY_DETAIL);
  if (response.data?.success && response.data?.data) {
    return response.data.data;
  }
  return null;
}

/**
 * Fetch agency sub-users (GET api/v2/agency/sub-users/).
 */
export async function getAgencySubUsers(): Promise<AgencySubUser[]> {
  const response = await apiClient.get<AgencySubUsersResponse>(API_ENDPOINTS.AUTH.AGENCY_SUB_USERS);
  if (response.data?.success && Array.isArray(response.data?.data)) {
    return response.data.data;
  }
  return [];
}

/**
 * Add a new artist under the agency
 * (POST api/v2/agency/artists/onboarding/).
 */
export async function addAgencyArtist(payload: AddAgencyArtistPayload): Promise<void> {
  await apiClient.post(API_ENDPOINTS.AUTH.AGENCY_ARTISTS_ONBOARDING, payload);
}

export interface CreateSubUserPayload {
  email: string;
  phone: string;
  country_code: string;
  full_name: string;
  password: string;
  can_edit: boolean;
  can_view: boolean;
  artist_slugs: string[];
}

export interface UpdateSubUserPayload {
  email: string;
  phone: string;
  country_code: string;
  full_name: string;
  can_edit: boolean;
  can_view: boolean;
  artist_slugs: string[];
  password?: string;
}

/**
 * Create a sub-user (POST api/v2/agency/sub-users/).
 */
export async function createSubUser(payload: CreateSubUserPayload): Promise<void> {
  await apiClient.post(API_ENDPOINTS.AUTH.AGENCY_SUB_USERS, payload);
}

/**
 * Update a sub-user (PATCH api/v2/agency/sub-users/:id/).
 */
export async function updateSubUser(id: number, payload: UpdateSubUserPayload): Promise<void> {
  await apiClient.patch(API_ENDPOINTS.AUTH.AGENCY_SUB_USER(id), payload);
}

/**
 * Delete a sub-user (DELETE api/v2/agency/sub-users/:id/).
 */
export async function deleteSubUser(id: number): Promise<void> {
  await apiClient.delete(API_ENDPOINTS.AUTH.AGENCY_SUB_USER(id));
}

/** Payload for remove-artist request; shape depends on managed_by (artist | family | manager | agency). */
export interface DeleteArtistFromAgencyPayload {
  artist_slug: string;
  reason: string;
  managed_by: 'artist' | 'family' | 'manager' | 'agency';
  full_name: string;
  email: string;
  contact_no: string;
  relation_to_artist?: string;
  agency_name?: string;
}

/**
 * Request to remove an artist from the agency (POST delete-artist-from-agency).
 * Payload must include full_name; relation_to_artist for family; agency_name for agency.
 */
export async function deleteArtistFromAgency(payload: DeleteArtistFromAgencyPayload): Promise<void> {
  await apiClient.post(API_ENDPOINTS.AUTH.DELETE_ARTIST_FROM_AGENCY, payload);
}
